import React from "react";

const ROWS = [
  ["`", "1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "-", "=", "back"],
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p", "[", "]"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l", ";", "'"],
  ["shift", "z", "x", "c", "v", "b", "n", "m", ",", ".", "/", "shift"],
];

const FINGER = {
  pinky: "#ff007f",
  ring: "#ffb000",
  middle: "#39ff14",
  index: "#00f0ff",
  thumb: "#a855f7",
};

const KEY_FINGER = {};
const assign = (keys, f) => keys.split("").forEach((k) => (KEY_FINGER[k] = f));
assign("`1qaz", "pinky");
assign("2wsx", "ring");
assign("3edc", "middle");
assign("45rtfgvb", "index");
assign("67yuhjnm", "index");
assign("8ik,", "middle");
assign("9ol.", "ring");
assign("0-=p[]';/", "pinky");
KEY_FINGER.shift = "pinky";
KEY_FINGER.back = "pinky";
KEY_FINGER.space = "thumb";

const SHIFTED = { "!": "1", "@": "2", "#": "3", "$": "4", "%": "5", "^": "6", "&": "7", "*": "8", "(": "9", ")": "0", "_": "-", "+": "=", ":": ";", '"': "'", "<": ",", ">": ".", "?": "/" };

export const fingerColor = (k) => FINGER[KEY_FINGER[k]] || "#64748b";

const LEFT_KEYS = new Set("`12345qwertasdfgzxcvb".split(""));

function handFor(key) {
  if (key === "space") return "both";
  if (key === "back") return "right";
  return LEFT_KEYS.has(key) ? "left" : "right";
}

// resolves a target char into {hand, finger, key, shift}
export function fingerFor(ch) {
  const r = resolveKey(ch);
  if (!r) return null;
  const finger = KEY_FINGER[r.key] || "index";
  return { ...r, finger, hand: handFor(r.key) };
}

// resolves a target char into {key, shift}
export function resolveKey(ch) {
  if (!ch) return null;
  if (ch === " ") return { key: "space", shift: false };
  if (ch === "\b") return { key: "back", shift: false };
  if (SHIFTED[ch]) return { key: SHIFTED[ch], shift: true };
  if (/[A-Z]/.test(ch)) return { key: ch.toLowerCase(), shift: true };
  return { key: ch.toLowerCase(), shift: false };
}

export { FINGER };

export function FingerKeyboard({ nextChar, highlightKeys = [], outlineKeys = [], compact = false }) {
  const target = resolveKey(nextChar);
  const hl = new Set(highlightKeys.map((k) => k.toLowerCase()));
  const ol = new Set(outlineKeys.map((k) => k.toLowerCase()));
  const isNext = (k) => target && (target.key === k || (target.shift && k === "shift"));
  const keyClass = compact ? "h-5 min-w-[1.25rem] text-[9px]" : "h-7 min-w-[1.75rem] text-xs";

  return (
    <div className="flex flex-col items-center gap-[3px] select-none" data-testid="finger-keyboard">
      {ROWS.map((row, ri) => (
        <div key={row[0]} className="flex gap-[3px]" style={{ marginLeft: ri * (compact ? 6 : 9) }}>
          {row.map((k, ki) => (
            <Key key={k === "shift" ? `shift-${ki}` : k} k={k} next={isNext(k)} focus={hl.has(k)} outline={ol.has(k)} className={keyClass} />
          ))}
        </div>
      ))}
      <div className="flex gap-[3px]">
        <Key k="space" next={isNext("space")} focus={false} className={`${keyClass} ${compact ? "w-40" : "w-60"}`} label="SPACE · FIRE" />
      </div>
    </div>
  );
}

const WIDE_KEYS = new Set(["shift", "back"]);
const KEY_GLYPH = { shift: "⇧", back: "⌫" };

function keyStyle({ next, focus, outline, color }) {
  const style = { borderColor: color, borderWidth: 1, color, background: "rgba(13,13,26,0.85)", opacity: 0.85 };
  if (outline) {
    style.borderColor = "#ffffff";
    style.borderWidth = 2;
    style.boxShadow = "0 0 6px rgba(255,255,255,0.7)";
  }
  if (focus) {
    style.color = "#06060e";
    style.background = color;
    style.opacity = 1;
  }
  if (next) {
    style.borderColor = "#ffe600";
    style.color = "#06060e";
    style.background = "#ffe600";
    style.opacity = 1;
    delete style.boxShadow;
  }
  return style;
}

function Key({ k, next, focus, outline, className, label }) {
  const color = fingerColor(k);
  const compact = className.includes("h-5");
  const wide = WIDE_KEYS.has(k) ? (compact ? "w-10" : "w-14") : "";
  const nextClass = next ? "tb-key-next scale-110 z-10" : "";
  return (
    <div
      data-testid={next ? "next-key" : undefined}
      data-key={k}
      data-outline={outline || undefined}
      className={`${className} ${wide} px-1 rounded-[3px] border font-typewriter font-bold uppercase flex items-center justify-center transition-all duration-100 ${nextClass}`}
      style={keyStyle({ next, focus, outline, color })}
    >
      {label || KEY_GLYPH[k] || k}
    </div>
  );
}

export function FingerLegend() {
  return (
    <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 font-vt text-sm" data-testid="finger-legend">
      {Object.entries(FINGER).map(([f, c]) => (
        <span key={f} className="flex items-center gap-1" style={{ color: c }}>
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: c }} />
          {f}
        </span>
      ))}
    </div>
  );
}
